/**
 * <s-product-thumbs> — Product gallery thumbnail rail
 * Wraps the horizontal media scroller and its thumbnail buttons.
 *
 * Markup contract:
 *   [data-gallery-track][id] — horizontal scroll-snap track holding [data-media-slide] children.
 *   button[commandfor=<track id>][command="--show-media"][data-media-index] — thumbnail buttons.
 *
 * Behavior:
 *   --show-media scrolls the track to the matching slide (instant when reduced motion is preferred).
 *   Scrolling the track updates aria-current on the thumbnail of the visible slide.
 *   Hovering a thumbnail with a mouse also shows its slide.
 */
class SProductThumbs extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    this.#ac = new AbortController();
    const slides = [...track.querySelectorAll('[data-media-slide]')];
    const thumbs = [...this.querySelectorAll('[data-media-index]')];

    // instant: jump straight to the slide and fade it in (used for hover) instead of sliding across.
    const showSlide = (index, instant = false) => {
      const slide = slides[index];
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const left = slide.offsetLeft - track.offsetLeft;
      if (instant) {
        // Snap-mandatory can turn a programmatic jump into a smooth glide; set the position directly.
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        track.style.scrollBehavior = '';
        if (!reduce) slide.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
      } else {
        track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
      }
      this.#setCurrent(thumbs, index);
    };

    track.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--show-media') return;
        showSlide(Number(event.source?.dataset.mediaIndex ?? 0));
      },
      { signal: this.#ac.signal }
    );

    // Hovering a thumbnail shows its slide in the main image; tapping still works on touch screens.
    this.addEventListener(
      'mouseover',
      (event) => {
        const thumb = event.target.closest?.('[data-media-index]');
        if (!thumb || !this.contains(thumb)) return;
        if (thumb.getAttribute('aria-current') === 'true') return;
        showSlide(Number(thumb.dataset.mediaIndex ?? 0), true);
      },
      { signal: this.#ac.signal }
    );

    // Selected variant → show its assigned image. Each slide lists its variants in data-variant-ids.
    const indexForVariant = (variantId) =>
      variantId ? slides.findIndex((slide) => (slide.dataset.variantIds || '').includes(` ${variantId} `)) : -1;
    const showVariantMedia = (variantId) => {
      const index = indexForVariant(variantId);
      if (index >= 0) showSlide(index, true);
    };

    // The variant picker lives outside this element, so listen for its option changes on the document.
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const picker = target.closest('s-variant-picker');
        if (!picker || picker.dataset.productId !== this.dataset.productId) return;
        const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
        if (variantId) showVariantMedia(variantId);
      },
      { signal: this.#ac.signal }
    );

    // A ?variant= link opens on that variant's image instead of the first product image.
    const initialIndex = indexForVariant(this.dataset.initialVariantId);
    if (initialIndex > 0) {
      requestAnimationFrame(() => {
        const slide = slides[initialIndex];
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = slide.offsetLeft - track.offsetLeft;
        track.style.scrollBehavior = '';
        this.#setCurrent(thumbs, initialIndex);
      });
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setCurrent(thumbs, slides.indexOf(entry.target));
          }
        }
      },
      { root: track, threshold: [0.6] }
    );
    slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #setCurrent(thumbs, index) {
    thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-thumbs')) {
  customElements.define('s-product-thumbs', SProductThumbs);
}
